/**
 * The order the employee list is in, and how to step through it.
 *
 * Two asks from the product user (2026-09-21): a search, because finding one person among many
 * is slow, and arrows from an open employee to the next one, because checking or filling in
 * details for a lot of people means walking back to the list every time.
 *
 * Both need the same order, or "next" would land somewhere the list did not show. So the order
 * lives here and both screens read it.
 */
import type { ApiUser } from '@/api/data';
import { fullName } from '@/ui/format';

/** Which people the list shows: the Ansatte select (manager redesign, 2026-10-09). */
export type Who = 'aktive' | 'inaktive' | 'alle';
export const whoOf = (v: string | null): Who => (v === 'inaktive' || v === 'alle' ? v : 'aktive');

/** People by name; active only unless asked, the way Ansatte has always listed them. */
export const roster = (users: ApiUser[] = [], who: Who = 'aktive'): ApiUser[] =>
  users
    .filter((u) => (who === 'alle' ? true : who === 'aktive' ? u.active_status : !u.active_status))
    .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb'));

/**
 * Whether a person matches what was typed. Name, username, e-mail and employee number, because
 * those are the four things somebody might have in front of them when they go looking. Accents
 * are folded, so "osterberg" finds "Østerberg"; a Norwegian roster is full of ø and å and
 * nobody wants to reach for them mid-search.
 */
const fold = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/ø/g, 'o')
    .replace(/æ/g, 'ae')
    .replace(/å/g, 'a');

export function matches(u: ApiUser, query: string): boolean {
  const q = fold(query.trim());
  if (!q) return true;
  // team and job title too (manager redesign, 2026-10-09): "kjøkken" finds the kitchen
  const hay = [
    fullName(u),
    u.username,
    u.email,
    u.employee_id,
    u.title,
    ...(u.teams ?? []).map((t) => t.name),
  ]
    .filter(Boolean)
    .map((v) => fold(String(v)))
    .join(' ');
  // every word must appear somewhere, so "ola nord" finds Ola Nordmann and "nord ola" does too
  return q.split(/\s+/).every((word) => hay.includes(word));
}

export interface Neighbours {
  prev: ApiUser | null;
  next: ApiUser | null;
  /** 1-based, for "3 av 24". 0 when the person is not in the list. */
  index: number;
  total: number;
}

/**
 * Who comes before and after this person, among the people with the same status: an active
 * person steps through the active list, an inactive one through the inactive list, so the
 * arrows follow the list the manager opened them from. Unknown: no arrows.
 */
export function neighbours(users: ApiUser[], id: number | undefined): Neighbours {
  const me = users.find((u) => u.id === id);
  const list = roster(users, me && !me.active_status ? 'inaktive' : 'aktive');
  const i = list.findIndex((u) => u.id === id);
  if (i < 0) return { prev: null, next: null, index: 0, total: list.length };
  return {
    prev: list[i - 1] ?? null,
    next: list[i + 1] ?? null,
    index: i + 1,
    total: list.length,
  };
}

/**
 * What is missing before this person can be paid and scheduled, one phrase each.
 *
 * Only for a reader who receives these fields and can fix them: judging a withheld field would
 * report every colleague as unfinished (TF-SIMPLE-ROLES acceptance, 2026-09-16).
 *
 * "Avtalt tid" has two ways of being wrong and they are not the same. Unset means nobody has
 * said yet. Zero or negative means somebody said something impossible, and that is worse,
 * because every hour the person works is then over their agreed week and every shift reports a
 * breach. 16 people on the production copy carried 0 or a negative number, counted 2026-09-21,
 * and none of them showed as unfinished, because a stored 0 is not null.
 */
export interface Gap {
  /** the Rediger ansatt field that fixes it */
  field: 'agreed_week_hours' | 'email' | 'employee_id' | 'birth_date';
  /** the short tag in the Oppsett column */
  tag: string;
  /** the whole phrase */
  text: string;
}

/** The one rule for "mangler oppsett": the roster, the profile and every link to them use it. */
export function setupItems(u: ApiUser, visible: boolean): Gap[] {
  if (!visible) return [];
  const gaps: Gap[] = [];
  const week = u.max_hours_per_week;
  if (week == null)
    gaps.push({ field: 'agreed_week_hours', tag: 'Avtalt tid', text: 'Avtalt tid ikke satt' });
  else if (week <= 0)
    gaps.push({ field: 'agreed_week_hours', tag: 'Avtalt tid 0', text: 'Avtalt tid er 0 eller mindre' });
  if (!u.email) gaps.push({ field: 'email', tag: 'E-post', text: 'Mangler e-post' });
  if (!u.employee_id) gaps.push({ field: 'employee_id', tag: 'Ansattnummer', text: 'Mangler ansattnummer' });
  // plan 0027 batch A: without it the person cannot be put on new shifts
  if (u.birth_date_missing)
    gaps.push({ field: 'birth_date', tag: 'Fødselsdato', text: 'Mangler fødselsdato' });
  return gaps;
}

/**
 * #54: no positive agreed week. The rules then count with the company's week, else 37,5
 * (backend services/agreedWeek.js effectiveWeekHours, FALLBACK_WEEK_HOURS). Undefined is a reader
 * who is not sent the field, and is not flagged.
 */
export const weekMissing = (u: Pick<ApiUser, 'max_hours_per_week'>): boolean =>
  u.max_hours_per_week !== undefined && (u.max_hours_per_week == null || u.max_hours_per_week <= 0);

/** The week the rules use for a person with none: the company's, else 37,5, as the backend does. */
export const ruleWeek = (companyWeek: number | null | undefined): number =>
  companyWeek != null && companyWeek > 0 ? companyWeek : 37.5;

export const setupGaps = (u: ApiUser, visible: boolean): string[] =>
  setupItems(u, visible).map((g) => g.text);

/**
 * Stillingsprosent (#108): the person's agreed week over the company's, rounded to a whole
 * percent. One source of truth, the agreed week; nothing stores the percentage. Null when either
 * week is unknown or not positive, so an unset week never reads as 100 %.
 */
export function stillingsprosent(
  u: Pick<ApiUser, 'max_hours_per_week'>,
  companyWeek: number | null | undefined,
): number | null {
  const w = u.max_hours_per_week;
  if (w == null || w <= 0 || companyWeek == null || companyWeek <= 0) return null;
  return Math.round((w / companyWeek) * 100);
}

export const EMPLOYMENT_FORMS: Record<'fast' | 'midlertidig' | 'tilkalling', string> = {
  fast: 'Fast',
  midlertidig: 'Midlertidig',
  tilkalling: 'Tilkalling',
};

/**
 * The end date against today (#108): 'passed' from the day after it, 'soon' in the 30 days
 * before, else null. The warning the issue asks for, shared by the roster and the card.
 */
export function endNotice(
  u: Pick<ApiUser, 'end_date'>,
  today: string,
): { kind: 'passed' | 'soon'; date: string } | null {
  const d = u.end_date?.slice(0, 10);
  if (!d) return null;
  if (d < today) return { kind: 'passed', date: d };
  const limit = new Date(`${today}T00:00:00Z`);
  limit.setUTCDate(limit.getUTCDate() + 30);
  return d <= limit.toISOString().slice(0, 10) ? { kind: 'soon', date: d } : null;
}
